import { useEffect, useRef, useState } from 'react';

export const useIsFirstRender = (framesToWait = 1) => {
  const [isFirstRender, setIsFirstRender] = useState(true);
  const rafId = useRef<number>();

  /**
   * Sometimes, we want to use this hook to conditionally apply styles to a component that should
   * only be applied after the first render. However, the timing at which styles are applied vs
   * when the component is mounted can be inconsistent.
   *
   * This hook uses requestAnimationFrame to wait for the specified number of frames before setting
   * the state to false. You need a bit of trial and error to determine the number of frames to wait
   * for consistent results.
   */
  useEffect(() => {
    let frame = 0;
    const tick = () => {
      frame++;
      if (frame >= framesToWait) {
        setIsFirstRender(false);
      } else {
        rafId.current = requestAnimationFrame(tick);
      }
    };
    rafId.current = requestAnimationFrame(tick);
    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [framesToWait]);

  return isFirstRender;
};
